import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon, Input, Upload, DatePicker, Tooltip, Form, notification, Announcement } from '@ss/mtd-react';
import { Select } from '@block/plug';
import useGo from '@utils/navigate';
import { cardChecking } from '@utils/documentChecking';
import { useRootStore } from '@src/model';
import { getCityList, getEnumByCategory, getIdTypeData, verifyIdentification } from '@service/resumeDetail';
import uploadHelper from '@utils/uploadAttachment';

import './index.scss';

const FormItem = Form.Item;

function BaseInfo(props) {
  const { type, data } = props;
  const { go } = useGo();
  const formRef = useRef();
  const titleRef = useRef();
  const { campusResumeFormRefStore, socialResumeFormRefStore, socialResumeDetail, campusResumeDetail } = useRootStore();
  let storeRef;
  let store;
  if (type === 'campus') {
    store = campusResumeDetail;
    storeRef = campusResumeFormRefStore;
  }
  if (type === 'social') {
    store = socialResumeDetail;
    storeRef = socialResumeFormRefStore;
  }

  /**
   * 初始化
   */
  useEffect(() => {
    storeRef.changeRef('resumeBaseInfoRef', formRef.current);
    if (data) {
      if (data?.identificationTypeCode === 'MILID') {
        data.identificationTypeCode = null;
        data.identificationType = null;
      }
      // @ts-ignore
      data && formRef.current?.setAllFieldsValue(data);
    }
  }, [data]);

  /**
   * 上传头像
   */
  const onUpload = async (file: any) => {
    uploadHelper('/api/official/resume/photo/upload', file, {
      headers: {
        'Content-Disposition': `attachment; filename="${encodeURI(file.name)}"`,
      },
      onSuccess: (res) => {
        store.setResumeBaseInfo('photoId', res.photoId);
        store.setResumeBaseInfo('photoUrl', res.photoUrl);
        notification.success({
          title: '成功',
          message: '上传照片成功',
        });
      },
      onError: (e) => {
        notification.error({
          title: '错误',
          message: e || '上传照片失败',
        });
      },
    });
  };

  /**
   * 修改表单值同步更新store的值
   */
  const handleValueChange = (formItemKey: string, formItemValue: any) => {
    // 如果是对象，就是select框，需要手动赋值
    switch (formItemKey) {
      case 'genderName': {
        store.setResumeBaseInfo('genderName', formItemValue?.name ? formItemValue.name : '');
        store.setResumeBaseInfo('gender', formItemValue?.id ? formItemValue.id : '');
        break;
      }
      case 'cityName': {
        store.setResumeBaseInfo('cityName', formItemValue?.name ? formItemValue.name : '');
        store.setResumeBaseInfo('cityId', formItemValue?.code ? formItemValue.code : '');
        break;
      }
      case 'identificationType': {
        store.setResumeBaseInfo('identificationType', formItemValue?.name ? formItemValue.name : '');
        store.setResumeBaseInfo('identificationTypeCode', formItemValue?.id ? formItemValue.id : '');

        // 如果选择了NOTA，清空证件号码，清除证件号码字段的验证错误提示
        if (formItemValue?.id === 'NOTA') {
          store.setResumeBaseInfo('identificationNo', '');
          // @ts-ignore
          formRef.current?.setFieldsValue({ identificationNo: '' });
          // @ts-ignore
          formRef.current?.clearValidateStatus(['identificationNo']);
        }
        // @ts-ignore
        if (store?.resumeBaseInfo?.identificationNo) formRef.current.validateFields(['identificationNo']);
        break;
      }
      case 'expectedCity': {
        store.setResumeBaseInfo('expectedCity', formItemValue?.name ? formItemValue.name : '');
        store.setResumeBaseInfo('expectedCityId', formItemValue?.code ? formItemValue.code : '');
        break;
      }
      case 'degreeName': {
        store.setResumeBaseInfo('degreeName', formItemValue?.name ? formItemValue.name : '');
        store.setResumeBaseInfo('degree', formItemValue?.id ? formItemValue.id : '');
        break;
      }
      case 'name': {
        store.setResumeBaseInfo(formItemKey, formItemValue);
        // @ts-ignore
        if (store?.resumeBaseInfo?.identificationNo) formRef.current.validateFields(['identificationNo']);
      }
      default:
        store.setResumeBaseInfo(formItemKey, formItemValue);
    }
  };

  /**
   * 获取枚举接口
   */
  const handleFetchData = async (category: string, filter: string) => {
    let res;
    if (category === 'city') res = await getCityList({ keyword: filter, type: 1 });
    else if (category === 'idType') res = await getIdTypeData();
    else res = await getEnumByCategory({ category, filter });
    if (category === 'idType' && filter) {
      res = res?.filter((v) => v?.name?.indexOf(filter || '') > -1);
    }
    return res;
  };

  return (
    <div className="base_info_edit ">
      {/*  @ts-ignore */}
      <div className="model_title" ref={titleRef}>
        基础信息
      </div>
      <Form
        // @ts-ignore
        ref={formRef}
        labelWidth="8em"
        className="base_info_form model_form"
        defaultFieldsValue={data}
        onFieldValueChange={handleValueChange}
      >
        <FormItem formItemKey="name" span="11" labelWidth={0} required message="请填写姓名">
          <div className="label">
            姓名<span className="icon">*</span>
          </div>
          <Input toFormItem size="large" placeholder="请输入你的真实姓名" maxLength={50} />
        </FormItem>
        <FormItem formItemKey="" span="11" labelWidth={0} />
        <FormItem formItemKey="avatar" span="11" labelWidth={0} className="avatar">
          <div className="label">个人照片</div>
          <Upload
            className={`upload-img-demo ${data?.photoUrl ? 'upload-img-demo-active' : ''}`}
            onUpload={onUpload}
            accept=".jpg,.png,.jpeg"
            multiple={false}
            draggable
          >
            {data?.photoUrl ? (
              <img className="avatar_img" src={data?.photoUrl} alt="" />
            ) : (
              <>
                <img className=" avatar_img avatar_img_hover" src={require('@imgs/avatar.png')} alt="" />
                <Icon className="upload-img-add" type="add" style={{ color: '#A3A3A3' }} />
              </>
            )}
          </Upload>
        </FormItem>
        <FormItem formItemKey="genderName" span="11" labelWidth={0}>
          <div className="label">性别</div>
          <Select
            toFormItem
            size="large"
            labelField="name"
            keyField="id"
            onFetchData={(filter) => handleFetchData('gender', filter)}
            placeholder="请选择"
          />
        </FormItem>
        <FormItem formItemKey="" span="12" labelWidth={0} />
        <FormItem
          formItemKey="interCode"
          span="3"
          className="form_item_phone_code"
          labelWidth={0}
          required
          message="请填写手机号码"
        >
          <div className="label label_phone">
            手机号码<span className="icon">*</span>
            <span>
              <Tooltip
                message={
                  <div className="resume_base_info_phone_tooltip">
                    请前往
                    <span onClick={() => go('/personalCenter/accountSetting')}>账号设置</span>
                    页面修改手机号码
                  </div>
                }
                autoDestory
              >
                <Icon type="info-circle-o" style={{ fontSize: 16 }} />
              </Tooltip>
            </span>
          </div>
          <Select
            toFormItem
            size="large"
            labelField="name"
            keyField="code"
            onFetchData={(filter) => handleFetchData('interCodeName', filter)}
            placeholder="请选择"
            disabled
          />
        </FormItem>
        <FormItem
          className="form_item_phone"
          formItemKey="phone"
          span="8"
          labelWidth={0}
          required
          message="请填写手机号码"
        >
          <div className="label"> </div>
          <Input toFormItem size="large" placeholder="请输入手机号码" disabled />
        </FormItem>
        <FormItem
          formItemKey="email"
          span="11"
          labelWidth={0}
          required
          rules={[
            {
              required: true,
              message: `${type === 'campus' ? '建议填写QQ邮箱' : '请填写邮箱'}`,
            },
            {
              pattern: /^([a-zA-Z0-9_.-])*([a-zA-Z0-9_-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/,
              message: '邮箱格式不正确',
            },
            {
              // @ts-ignore
              pattern: '^[\\s\\S]{0,200}$',
              message: '请确保填写字数不超过200',
            },
          ]}
        >
          <div className="label">
            邮箱<span className="icon">*</span>
          </div>
          <Input toFormItem size="large" placeholder={type === 'campus' ? '建议填写QQ邮箱' : '请输入你的邮箱地址'} />
        </FormItem>

        {type === 'campus' && (
          <FormItem
            formItemKey="identificationType"
            className="form_item_identification_code"
            span="3"
            labelWidth={0}
            required
            message="请填写证件类型"
          >
            <div className="label">
              证件号码<span className="icon">*</span>
            </div>
            <Select
              toFormItem
              size="large"
              placeholder="请选择证件类型"
              labelField="name"
              keyField="id"
              onFetchData={(filter) => handleFetchData('idType', filter)}
            />
          </FormItem>
        )}
        {type === 'campus' && (
          <FormItem
            className="form_item_identification"
            formItemKey="identificationNo"
            span="8"
            labelWidth={0}
            required={store?.resumeBaseInfo?.identificationTypeCode !== 'NOTA'}
            message="请填写证件号码"
            rules={[
              {
                validator: async (rule, tmp, callback) => {
                  // @ts-ignore
                  const idTypeCode = store?.resumeBaseInfo?.identificationTypeCode;
                  const resumeName = store?.resumeBaseInfo?.name;
                  const value = tmp?.trim();
                  // 如果证件类型是NOTA，跳过验证
                  if (idTypeCode === 'NOTA') {
                    callback();
                    return;
                  }
                  if (!value) {
                    callback('请填写证件号码');
                    return;
                  }
                  cardChecking(value, idTypeCode, callback);
                  if (['NID', 'HK/MA', 'HXZ'].includes(idTypeCode) && resumeName) {
                    const params = {
                      name: resumeName,
                      identificationType: idTypeCode,
                      identificationNumber: value,
                    };
                    try {
                      const res = await verifyIdentification(params);
                      if (res?.resumeVerifyStatus === '0') {
                        callback('证件信息与姓名不匹配');
                      }
                    } catch (e) {
                      console.log('表单出错：', e);
                      callback('');
                    }
                  }
                  callback();
                },
              },
            ]}
          >
            <div className="label"> </div>
            <Input
              toFormItem
              size="large"
              // placeholder="请输入证件号码"
              placeholder={
                store?.resumeBaseInfo?.identificationTypeCode === 'NOTA' ? '选择此证件类型无需填写' : '请输入证件号码'
              }
              disabled={store?.resumeBaseInfo?.identificationTypeCode === 'NOTA'}
            />
          </FormItem>
        )}
        {type === 'social' && (
          <FormItem formItemKey="birthday" span="11" labelWidth={0} required message="请填写出生年月">
            <div className="label">
              出生年月<span className="icon">*</span>
            </div>
            <DatePicker
              toFormItem
              size="large"
              valueFormat="timestamp"
              format="YYYY/MM"
              placeholder="选择日期"
              disabledDate={(cur) => {
                // 当前时间加一个月
                return cur > Number(new Date()) + 2592000000;
              }}
            />
          </FormItem>
        )}

        <FormItem formItemKey="cityName" span="11" labelWidth={0} required message="请填写当前居住城市">
          <div className="label">
            当前居住城市
            <span className="icon">*</span>
            <span>
              <Tooltip message="若海外城市无法选择，请输入其他">
                <Icon style={{ fontSize: 16 }} type="info-circle-o" />
              </Tooltip>
            </span>
          </div>
          <Select
            toFormItem
            size="large"
            labelField="name"
            keyField="code"
            onFetchData={(filter) => handleFetchData('city', filter)}
            placeholder="请输入"
          />
        </FormItem>
        {type === 'campus' && (
          <FormItem labelWidth={0}>
            <Announcement
              className="campus-identification-tip-wrap"
              title={
                <div className="campus-identification-tip-inner">
                  每人仅允许注册和使用一份简历应聘，在个人简历信息注册完成后，使用本人其他类型证件(如护照、港澳永久身份证、台胞证等)注册新简历的行为，或通过修改及编纂身份证号以新增注册简历的行为，将被视为重复注册和使用简历应聘。
                  <strong>重复注册简历应聘的行为一经发现将取消当前面试和录用资格</strong>。<br />
                  若出现证件信息被占用的情况，请发邮件到campus@meituan.com邮箱说明。
                </div>
              }
              showIcon
              type="warning"
            />
          </FormItem>
        )}
        {type === 'social' && (
          <FormItem formItemKey="startWorkYear" span="11" labelWidth={0} required message="请填写工作年份">
            <div className="label">
              工作年份<span className="icon">*</span>
            </div>
            <DatePicker
              toFormItem
              size="large"
              format="YYYY"
              valueFormat="timestamp"
              placeholder="请选择"
              disabledDate={(cur) => {
                // 当前时间加+1年
                return cur > Number(new Date()) + 31104000000;
              }}
            />
          </FormItem>
        )}
        {type === 'social' && (
          <FormItem formItemKey="degreeName" span="11" labelWidth={0} required message="请填写最高学历">
            <div className="label">
              最高学历<span className="icon">*</span>
            </div>
            <Select
              toFormItem
              size="large"
              labelField="name"
              keyField="id"
              onFetchData={(filter) => handleFetchData('diploma', filter)}
              placeholder="请选择"
            />
          </FormItem>
        )}
        {type === 'social' && (
          <FormItem formItemKey="expectedCity" span="11" labelWidth={0} required message="请填写期望城市">
            <div className="label">
              期望城市<span className="icon">*</span>
            </div>
            <Select
              toFormItem
              size="large"
              placeholder="请选择"
              labelField="name"
              keyField="code"
              onFetchData={(filter) => handleFetchData('city', filter)}
            />
          </FormItem>
        )}
      </Form>
    </div>
  );
}

export default observer(BaseInfo);
